import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { SettingsService } from '../settings.service';
import { LoginService } from '../login.service';
import { FinancialService } from '../financial.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-performer-price-list',
  templateUrl: './performer-price-list.component.html',
  styleUrls: ['./performer-price-list.component.scss'],
  standalone: false
})
export class PerformerPriceListComponent implements OnInit, OnChanges {
  @Input() selectedUserId: number | null = null;
  prices: any[] = [];
  allExams: any[] = [];
  examsSettings: any[] = [];
  error = false;
  errorMessage = '';
  newPrice: any = this.emptyPrice();
  byRoom = false;
  performerUID: any = null;

  constructor(
    public settingsService: SettingsService,
    public ls: LoginService,
    public financialService: FinancialService,
    private translate: TranslateService
  ) { }

  get performers(): any[] {
    return Array.isArray(this.ls.clinicUsers) ? this.ls.clinicUsers : [];
  }

  get currentUID(): any {
    return this.byRoom ? this.performerUID : this.ls.doctor;
  }

  examTypeLabel(exam: any): string {
    const key = 'appointments.gui.exams.' + exam.examType;
    const translated = this.translate.instant(key);
    return translated === key ? exam.examType : translated;
  }

  async onPerformerChange() {
    this.error = false;
    this.errorMessage = '';
    this.newPrice = this.emptyPrice();
    await this.loadPrices();
  }

  async ngOnInit() {
    this.byRoom = !!this.ls?.premissions?.ApptsByRoom;
    if (this.byRoom) {
      const current = this.performers.find((user) => String(user.UID) === String(this.ls.doctor));
      this.performerUID = current ? current.UID : this.performers[0]?.UID ?? null;
    }
    const exams = await this.settingsService.getExams();
    this.allExams = Array.isArray(exams) ? exams : [];
    this.examsSettings = Array.isArray(exams)
      ? exams.filter((exam) => !exam.custom && !this.financialService.combinedExamTypes.includes(exam.examType))
      : [];
    await this.loadPrices();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedUserId && !changes.selectedUserId.firstChange) {
      this.error = false;
      this.errorMessage = '';
      this.newPrice = this.emptyPrice();
      await this.loadPrices();
    }
  }

  sameId = (a: any, b: any) => String(a) === String(b);

  hasExam(examId: any): boolean {
    return this.allExams.some((exam) => this.sameId(exam.AutoIndex, examId));
  }

  emptyPrice() {
    return { examId: null, payer: null, reimbursementPrice: null, remunerationPercent: null, remunerationSum: null };
  }

  get doctorExams(): any[] {
    if (this.byRoom) {
      const seen = new Set<string>();
      return this.examsSettings.filter((exam) => {
        if (seen.has(exam.examType)) return false;
        seen.add(exam.examType);
        return true;
      });
    }
    const uid = String(this.ls.doctor);
    return this.examsSettings.filter((exam) => {
      if (!exam.users) return true;
      return String(exam.users).split(',').map((item) => item.trim()).includes(uid);
    });
  }

  async loadPrices() {
    if (!this.currentUID) {
      this.prices = [];
      return;
    }
    this.prices = await this.financialService.getPriceList(this.currentUID);
  }

  private isBlank(value: any): boolean {
    return value === null || value === undefined || value === '';
  }

  onPercentChange(price: any) {
    const base = Number(price.reimbursementPrice) || 0;
    price.remunerationSum = this.isBlank(price.remunerationPercent)
      ? null
      : Math.round(base * Number(price.remunerationPercent)) / 100;
  }

  onSumChange(price: any) {
    const base = Number(price.reimbursementPrice) || 0;
    price.remunerationPercent = this.isBlank(price.remunerationSum) || base <= 0
      ? null
      : Math.round((Number(price.remunerationSum) / base) * 10000) / 100;
  }

  onBaseChange(price: any) {
    if (!this.isBlank(price.remunerationPercent)) {
      this.onPercentChange(price);
    }
  }

  private payload(price: any) {
    const body: any = {
      examId: Number(price.examId),
      payer: price.payer,
      reimbursementPrice: Number(price.reimbursementPrice)
    };
    if (!this.isBlank(price.remunerationPercent)) {
      body.remunerationPercent = Number(price.remunerationPercent);
    }
    if (!this.isBlank(price.remunerationSum)) {
      body.remunerationSum = Number(price.remunerationSum);
    }
    return body;
  }

  private isValid(price: any): boolean {
    const hasRemuneration = !this.isBlank(price.remunerationPercent) || !this.isBlank(price.remunerationSum);
    return !!price.examId && !!price.payer && !this.isBlank(price.reimbursementPrice) && hasRemuneration;
  }

  private fail(error: any) {
    this.error = true;
    this.errorMessage = error?.error?.error || '';
  }

  async saveNew() {
    if (!this.isValid(this.newPrice)) {
      this.error = true;
      this.errorMessage = '';
      return;
    }
    try {
      await this.financialService.createPrice({ ...this.payload(this.newPrice), UID: this.currentUID });
      this.error = false;
      this.newPrice = this.emptyPrice();
      await this.loadPrices();
    } catch (error) {
      this.fail(error);
    }
  }

  edit(price: any) {
    price.isEditing = true;
  }

  async saveChanges(price: any) {
    if (!this.isValid(price)) {
      this.error = true;
      this.errorMessage = '';
      return;
    }
    try {
      const result = await this.financialService.updatePrice({ ...this.payload(price), AutoIndex: price.AutoIndex });
      price.remunerationPercent = result.remunerationPercent;
      price.remunerationSum = result.remunerationSum;
      price.isEditing = false;
      this.error = false;
    } catch (error) {
      this.fail(error);
    }
  }

  async remove(price: any) {
    try {
      await this.financialService.deletePrice(price.AutoIndex);
      this.error = false;
      await this.loadPrices();
    } catch (error) {
      this.fail(error);
    }
  }
}
